import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { orderBy } from 'lodash';
import { Guid } from 'guid-typescript';

@Module({ name: 'paths' })
export default class PathStore extends VuexModule {
  // there are 10000 meters squared per hectare
  m2ToHectaresFactor = 10000;

  paths: Array<SimplePath> = [];
  selectedPath: SimplePath | null = null;
  selectedPathLengthInMeters: number | null = null;
  selectedWorkAreaAreaInSqMeters: number | null = null;
  selectedCropAreaAreaInSqMeters: number | null = null;
  pathAssociatedField: FieldOperationsModel | null = null;
  currentLine: google.maps.LatLng[] = [];
  currentWorkArea: google.maps.LatLng[] = [];
  currentCropArea: google.maps.LatLng[] = [];
  editable = false;
  editWorkArea = false;
  editCropArea = false;
  clearMap = false;
  addPath = false;
  revertPath = false;
  revertWorkArea = false;
  deleteWorkArea = false;
  revertCropArea = false;
  deleteCropArea = false;
  isSelectedPathSimplified = false;
  selectedSimplifiedPath: SimplePath | null = null;

  @Mutation
  setSelectedPath(path: SimplePath) {
    this.selectedPath = path;
    this.editable = false;
    this.isSelectedPathSimplified = false;
    this.selectedPathLengthInMeters = 0;
    this.selectedWorkAreaAreaInSqMeters = 0;
    this.selectedCropAreaAreaInSqMeters = 0;
    this.currentLine = [];
  }

  @Mutation
  setSimplifiedPath(path: SimplePath) {
    this.selectedSimplifiedPath = path;
    this.isSelectedPathSimplified = true;
    this.selectedPathLengthInMeters = 0;
    this.selectedWorkAreaAreaInSqMeters = 0;
    this.selectedCropAreaAreaInSqMeters = 0;
    this.currentLine = [];
  }

  @Mutation
  revertSimplifiedPath() {
    if (this.isSelectedPathSimplified) {
      this.selectedSimplifiedPath = null;
      this.isSelectedPathSimplified = false;
      // trigger the map to redraw the original selected path
      this.selectedPath = Object.assign({}, this.selectedPath);
    }
  }

  @Mutation
  updateCurrentLine(newline: google.maps.Polyline) {
    this.currentLine = newline.getPath().getArray();
  }
  @Mutation
  setAvailablePaths(paths: Array<SimplePath>) {
    this.paths = orderBy(paths, [o => o.FieldID == Guid.EMPTY, o => o.Name], ['desc', 'asc']);
  }
  @Mutation
  toggleEditable() {
    this.editable = !this.editable;
  }
  @Mutation
  setSelectedPathLength(pathLength: number) {
    this.selectedPathLengthInMeters = pathLength;
  }
  @Mutation
  setSelectedWorkAreaArea(area: number) {
    this.selectedWorkAreaAreaInSqMeters = area;
  }
  @Mutation
  setSelectedCropAreaArea(area: number) {
    this.selectedCropAreaAreaInSqMeters = area;
  }
  @Mutation
  setSelectedField(field: FieldOperationsModel) {
    this.pathAssociatedField = field;
  }
  @Mutation
  triggerClearMap() {
    this.clearMap = !this.clearMap;
  }
  @Mutation
  setAddPathMode(value: boolean) {
    this.addPath = value;
  }
  @Mutation
  triggerRevertPath() {
    this.revertPath = !this.revertPath;
  }

  @Mutation
  triggerRevertWorkArea() {
    this.revertWorkArea = !this.revertWorkArea;
  }
  @Mutation
  setEditWorkArea(value: boolean) {
    this.editWorkArea = value;
  }
  @Mutation
  triggerWorkAreaDelete() {
    this.currentWorkArea = [];
    this.selectedWorkAreaAreaInSqMeters = 0;
    this.deleteWorkArea = !this.deleteWorkArea;
  }
  @Mutation
  setCurrentWorkArea(workArea: google.maps.LatLng[]) {
    this.currentWorkArea = workArea;
  }

  @Mutation
  triggerRevertCropArea() {
    this.revertCropArea = !this.revertCropArea;
  }
  @Mutation
  setEditCropArea(value: boolean) {
    this.editCropArea = value;
  }
  @Mutation
  triggerCropAreaDelete() {
    this.currentCropArea = [];
    this.selectedCropAreaAreaInSqMeters = 0;
    this.deleteCropArea = !this.deleteCropArea;
  }
  @Mutation
  setCurrentCropArea(cropArea: google.maps.LatLng[]) {
    this.currentCropArea = cropArea;
  }

  get availablePaths(): Array<SimplePath> {
    return this.paths;
  }
  get path(): SimplePath | null {
    return this.selectedPath;
  }
  get edit(): boolean {
    return this.editable;
  }
  get currentPath(): google.maps.LatLng[] {
    return this.currentLine;
  }
  get addPathMode(): boolean {
    return this.addPath;
  }
  get associatedField(): FieldOperationsModel {
    return this.pathAssociatedField as FieldOperationsModel;
  }
  get workArea(): google.maps.LatLng[] {
    return this.currentWorkArea;
  }
  get workAreaAreaHectares(): number {
    if (this.selectedWorkAreaAreaInSqMeters != null) {
      return this.selectedWorkAreaAreaInSqMeters / this.m2ToHectaresFactor;
    }
    return 0;
  }
  get cropArea(): google.maps.LatLng[] {
    return this.currentCropArea;
  }
  get cropAreaAreaHectares(): number {
    if (this.selectedCropAreaAreaInSqMeters != null) {
      return this.selectedCropAreaAreaInSqMeters / this.m2ToHectaresFactor;
    }
    return 0;
  }
}
